/**
 * React provider exposing the signed-in user + auth actions to the app.
 *
 * On mount:
 *   1. If localStorage has a refresh token, try `/auth/refresh` to mint an
 *      access token, then `/auth/me` to hydrate the live user. Cached user
 *      renders instantly while these calls run.
 *   2. Otherwise, status='unauthenticated' → AuthGuard sends to /login.
 *
 * After login/logout: status flips, AuthGuard either renders children or
 * redirects accordingly. All routes outside /login require authentication.
 */

import * as React from 'react';
import { authApi } from '@/services/auth';
import { session } from '@/services/session';
import type {
  CurrentUser,
  LoginPayload,
  LoginResult,
  MfaVerifyPayload,
} from '@/types/auth';
import { isMfaRequired } from '@/types/auth';

type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

interface AuthContextValue {
  status: AuthStatus;
  user: CurrentUser | null;
  login: (payload: LoginPayload) => Promise<LoginResult>;
  mfaVerify: (payload: MfaVerifyPayload) => Promise<LoginResult>;
  logout: () => Promise<void>;
}

const AuthContext = React.createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  // Start from the persisted user if we have one — gives an instant render
  // and avoids a /login flicker for returning users.
  const [user, setUser] = React.useState<CurrentUser | null>(() =>
    session.cachedUser(),
  );
  // Phase 2 SSO: start in 'loading' even without localStorage — the boot
  // refresh might succeed via the cross-subdomain HttpOnly cookie. The
  // refresh either lands us at 'authenticated' or flips to
  // 'unauthenticated' and AuthGuard sends us to /login.
  const [status, setStatus] = React.useState<AuthStatus>('loading');

  // Keep state in sync with session storage. Specifically: when api-client
  // clears the session after a refresh failure, this listener flips status
  // to 'unauthenticated' so AuthGuard redirects to /login.
  React.useEffect(() => {
    const unsub = session.subscribe((s) => {
      setUser(s?.user ?? null);
      if (!s) setStatus('unauthenticated');
    });
    return unsub;
  }, []);

  // Boot: always attempt /auth/refresh. Two cases:
  //   1) localStorage has a refresh token → send it in the body.
  //   2) No localStorage but the cross-subdomain cookie is present
  //      (Phase 2 SSO — user logged in at zafroi.com, opened
  //      kosh.zafroi.com) → cookie is auto-sent via withCredentials.
  // Either succeeds and we hydrate the user via /auth/me. Failures (401
  // or network) flip to 'unauthenticated' and AuthGuard sends to /login.
  React.useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const localToken = session.getRefreshToken() ?? undefined;
        const r = await authApi.refresh(localToken);
        session.updateAccessToken(r.accessToken, r.refreshToken, r.expiresAt);
        const me = await authApi.me(r.accessToken);
        if (cancelled) return;
        session.setUser(me);
        setStatus('authenticated');
      } catch {
        if (cancelled) return;
        session.clear();
        setStatus('unauthenticated');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = React.useCallback(async (payload: LoginPayload) => {
    const result = await authApi.login(payload);
    if (!isMfaRequired(result)) {
      session.set(result);
      setStatus('authenticated');
    }
    return result;
  }, []);

  const mfaVerify = React.useCallback(async (payload: MfaVerifyPayload) => {
    const result = await authApi.mfaVerify(payload);
    if (!isMfaRequired(result)) {
      session.set(result);
      setStatus('authenticated');
    }
    return result;
  }, []);

  const logout = React.useCallback(async () => {
    const active = session.current();
    try {
      if (active?.accessToken) await authApi.logout(active.accessToken);
    } catch {
      // Best-effort — even if the server-side call fails we still clear
      // local state.
    }
    session.clear();
    setStatus('unauthenticated');
  }, []);

  const value = React.useMemo<AuthContextValue>(
    () => ({ status, user, login, mfaVerify, logout }),
    [status, user, login, mfaVerify, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = React.useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside an AuthProvider');
  return ctx;
}
